import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Steps } from 'antd';
import { get, set, includes, filter, uniq, forEach, map } from 'lodash';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

import helpers from 'utils/helpers';
import { StyledStep } from 'components/Styled';
import Quotation from './Quotation';
import Pickup from './Pickup';
import Address from './Address';
import Items from './Items';
import Payment from './Payment';
import Finish from './Finish';

import Popup from 'components/common/Popup';

import { getPostsRequest } from 'ducks/shipments';

const stepName = [
  'quotation',
  'items',
  'address',
  'pickup',
  'payment',
  'finish'
];

class CreateShipment extends Component {
  state = {
    posts: false,
    popupKey: false,
    currency: 'HKD'
  };

  componentDidMount() {
    this.props.getPostsRequest();
  }

  determineBusinessOrPersonal = step => {
    // legacy function used for demine business or personal portal. business portal is removed.
    return this.props.history.push(
      `${process.env.PUBLIC_URL}/${localStorage.i18nextLng}/create-shipment/${stepName[step]}`
    );
  };

  handleClick = i => {
    this.determineBusinessOrPersonal(i);
  };

  handleQuotationNext = data => {
    const { origin, destination, service, boxData, totalRate, currency } = data;

    this.setState({ currency });

    localStorage.setItem(
      'singleOrder',
      JSON.stringify({ fromCountry: origin, toCountry: destination, boxData })
    );

    window.dataLayer.push({
      event: 'Add To Cart',
      selectedRate: totalRate,
      ecommerce: {
        currencyCode: 'HKD',
        add: {
          products: [
            {
              id: `${origin}-${destination}`,
              name: `${origin}-${destination}`,
              price: totalRate,
              brand: service,
              category: helpers.getWeightCategory(boxData.totalWeight),
              quantity: 1
            }
          ]
        }
      }
    });
    this.setState({ quotation: data }, () =>
      this.determineBusinessOrPersonal(1)
    );
  };

  handleItemsNext = data => {
    this.setState({ items: data }, () => this.determineBusinessOrPersonal(2));
  };

  handleAddressNext = (data, carrierService) => {
    this.setState(
      {
        address: data,
        quotation: {
          ...this.state.quotation,
          service: carrierService.slug
        },
        carrierService
      },
      () => this.determineBusinessOrPersonal(3)
    );
  };

  handlePickupNext = data => {
    const {
      origin,
      destination,
      service,
      boxData,
      totalRate
    } = this.state.quotation;

    window.dataLayer.push({
      event: 'Confirm Shipment',
      ecommerce: {
        checkout: {
          products: [
            {
              id: `${origin}-${destination}`,
              name: `${origin}-${destination}`,
              price: totalRate,
              brand: service,
              category: helpers.getWeightCategory(boxData.totalWeight),
              quantity: 1
            }
          ]
        }
      }
    });
    this.setState(
      {
        pickup: data
      },
      () => this.determineBusinessOrPersonal(4)
    );
  };

  handlePaymentPrev = () => {
    this.determineBusinessOrPersonal(3);
  };

  completeShipment = data => {
    const { origin, destination, service, boxData } = this.state.quotation;
    const trackingNumber = this.state.pickup.shipmentData?.trackingNumber;
    const totalRate =
      this.state.pickup.shipmentData?.naturalAmount?.totalRate ||
      this.state.pickup.shipmentData.totalRate;

    localStorage.removeItem('singleOrder');

    // TODO: fix GA for multi-currency
    window.dataLayer.push({
      event: 'Payment Success',
      transactionRate: totalRate,
      ecommerce: {
        purchase: {
          actionField: {
            id: trackingNumber,
            revenue: totalRate,
            tax: 0,
            shipping: 0,
            coupon: data.coupon
          },
          products: [
            {
              id: `${origin}-${destination}`,
              name: `${origin}-${destination}`,
              price: totalRate,
              brand: service,
              category: helpers.getWeightCategory(boxData.totalWeight),
              quantity: 1
            }
          ]
        }
      },
      pixel: {
        value: totalRate,
        currency: 'HKD',
        content_name: `${origin}-${destination}`,
        content_category: service
      }
    });

    this.setState(
      {
        trackingNumber: trackingNumber,
        orderRef: data.orderRef,
        discount: data.discount,
        pickupNumber: data.pickupNumber
      },
      () => this.determineBusinessOrPersonal(5)
    );
  };

  openPopup = posts => {
    // store to local storage only show popup once
    let local;
    try {
      local = JSON.parse(localStorage.getItem('posts')) || [];
    } catch (e) {
      local = [];
      console.error(e);
    }

    const list = map(posts, 'id');
    const read = [];

    forEach(list, x => {
      if (includes(local, x)) read.push(x);
    });

    posts = filter(posts, x => !includes(read, x.id));

    if (!posts || !posts.length) return;
    localStorage.setItem('posts', JSON.stringify(uniq([...local, ...list])));

    this.setState({
      posts,
      popupKey: !this.state.popupKey
    });
  };

  saveOrderState = (redirectUrl, data, props) => {
    const orderRef = get(this.state.address, 'orderRef');
    // save state
    localStorage.setItem(`order-${orderRef}`, JSON.stringify(this.state));
    // save data for completeShipment
    localStorage.setItem(`order-${orderRef}-data`, JSON.stringify(data));
    localStorage.setItem(`order-${orderRef}-props`, JSON.stringify(props));
    window.location.href = redirectUrl;
  };

  clearOrderStorage = () => {
    // clear orders state
    const arr = [];

    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i).substring(0, 6) === 'order-') {
        arr.push(localStorage.key(i));
      }
    }

    for (let i = 0; i < arr.length; i++) {
      localStorage.removeItem(arr[i]);
    }
  };

  restoreOrderState = ({ local, data, props }) => {
    set(props, 'campaigns.suppressPopup', true);
    this.props.setReduxState(props);
    this.setState(
      {
        ...local
      },
      () => {
        this.completeShipment(data);
        this.clearOrderStorage();
      }
    );
  };

  handler = {
    openPopup: this.openPopup,
    saveOrderState: this.saveOrderState,
    restoreOrderState: this.restoreOrderState,
    clearOrderStorage: this.clearOrderStorage
  };

  getSteps = () => {
    const steps = [
      {
        content: (
          <Quotation
            handleNext={this.handleQuotationNext}
            location={this.props.location}
            handler={this.handler}
          />
        )
      },
      {
        content: (
          <Items
            currency={this.state.currency}
            handleNext={this.handleItemsNext}
            determineBusinessOrPersonal={this.determineBusinessOrPersonal}
            redirect={this.state.quotation ? false : true}
            quotation={this.state.quotation}
            origin={get(this.state.quotation, 'origin')}
            destination={get(this.state.quotation, 'destination')}
            carrier={get(this.state.quotation, 'carrier')}
            boxData={get(this.state.quotation, 'boxData')}
            handler={this.handler}
          />
        )
      },
      {
        content: (
          <Address
            handleNext={this.handleAddressNext}
            origin={get(this.state.quotation, 'origin')}
            destination={get(this.state.quotation, 'destination')}
            carrier={get(this.state.quotation, 'carrier')}
            service={get(this.state.quotation, 'service')}
            boxData={get(this.state.quotation, 'boxData')}
            itemsData={get(this.state.items, 'itemsData')}
            itemType={get(this.state.items, 'itemType')}
            isDdp={get(this.state.items, 'isDdp')}
            selectedService={get(this.state.quotation, 'selectedService') ?? ''}
            redirect={this.state.quotation ? false : true}
            handler={this.handler}
            quotation={this.state.quotation}
          />
        )
      },
      {
        content: (
          <Pickup
            handleNext={this.handlePickupNext}
            quotation={this.state.quotation}
            origin={get(this.state.quotation, 'origin')}
            destination={get(this.state.quotation, 'destination')}
            carrier={get(this.state.quotation, 'carrier')}
            service={get(this.state.quotation, 'service')}
            selectedService={get(this.state.quotation, 'selectedService') ?? ''}
            redirect={this.state.quotation ? false : true}
            handler={this.handler}
            boxData={get(this.state.quotation, 'boxData')}
            shipmentData={get(this.state.address, 'shipmentData')}
            availableData={get(this.state.address, 'availableData')}
            orderRef={get(this.state.address, 'orderRef')}
          />
        )
      }
    ];

    return steps;
  };

  renderContent = () => {
    switch (this.props.currentStep) {
      case 0:
        // Step counter
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              flex: '1 1 auto'
            }}>
            <div
              style={{
                margin: '0 auto',
                width: '100%',
                maxWidth: `${process.env.REACT_APP_MAX_WIDTH}px`,
                overflow: 'hidden',
                flex: '0 0 auto'
              }}>
              {this.props.currentStep !== 0 && (
                <div
                  style={{
                    padding: '18px 5px',
                    backgroundColor: '#fafafa',
                    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.24)',
                    marginBottom: '5px'
                  }}>
                  <Steps
                    current={this.props.currentStep}
                    direction="horizontal"
                    labelPlacement="vertical">
                    {this.getSteps().map((step, i) => (
                      <StyledStep
                        key={this.props.t(`shipment.steps.${i}`)}
                        title={this.props.t(`shipment.steps.${i}`)}
                        style={{
                          cursor:
                            i < this.props.currentStep ? 'pointer' : 'auto'
                        }}
                        onClick={() => {
                          if (i < this.props.currentStep) {
                            this.handleClick(i);
                          }
                        }}
                      />
                    ))}
                  </Steps>
                </div>
              )}
            </div>
            {this.getSteps().map((step, i) => (
              <div
                key={`step${i + 1}_content`}
                style={{
                  display: this.props.currentStep === i ? 'flex' : 'none',
                  flex: '1 0 auto',
                  flexDirection: 'column'
                }}>
                {i <= this.props.currentStep ? step.content : null}
              </div>
            ))}
          </div>
        );

        default:
          return 'Not available';
    }
  };

  render() {
    return (
      <>
        <Helmet>
          <title>{this.props.t('shipment.header.title')}</title>
          <meta
            name="description"
            content={this.props.t('shipment.header.meta.description')}
          />
        </Helmet>
        {this.state.posts && (
          <Popup
            key={!!this.state.popupKey}
            type="post"
            popups={this.state.posts}
          />
        )}
        {this.renderContent()}
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    user: state.auth.user || false,
    logoutSuccess: !!state.auth.logoutSuccess
  };
};

const SET_REDUX_STATE = 'SET_REDUX_STATE';

const mapDispatchToProps = dispatch => {
  return {
    getPostsRequest: params => dispatch(getPostsRequest(params)),
    setReduxState: props =>
      dispatch({
        type: SET_REDUX_STATE,
        props
      })
  };
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(CreateShipment))
);
